<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020-2025 (original work) Open Assessment Technologies SA ;

    import { __ } from '@oat-sa-private/ui-core';
    import { Icon } from '@oat-sa-private/ui-elements';
    import { createEventDispatcher } from 'svelte';

    //possible step states
    const stepStates = Object.freeze({
        default: 'default',
        visited: 'visited',
        completed: 'completed',
        disabled: 'disabled',
        // with score:
        correct: 'correct',
        incorrect: 'incorrect',
        partial: 'partial',
        // with externalScored true:
        visitedWaiting: 'visitedWaiting',
        completedWaiting: 'completedWaiting'
    });

    //different space modes
    const spaceModes = Object.freeze({
        medium: 'medium',
        large: 'large'
    });

    //translatable state to be added to step's aria-label
    const stateAriaLabels = Object.freeze({
        [stepStates.visited]: __('visited'),
        [stepStates.completed]: __('completed'),
        [stepStates.correct]: __('correct'),
        [stepStates.incorrect]: __('incorrect'),
        [stepStates.partial]: __('partial'),
        [stepStates.visitedWaiting]: __('waiting'),
        [stepStates.completedWaiting]: __('waiting')
    });

    //gets the label text based on props and index
    const getLabel = (step, index) => step.label || index + 1;

    //gets the state based on props
    const getState = step => stepStates[step.state] || stepStates.default;

    //gets badge icon
    const getBadgeIcon = step => {
        switch (getState(step)) {
            case 'correct':
                return 'check-12';
            case 'incorrect':
                return 'wrong-12';
            case 'partial':
                return 'partial-12';
            case 'visitedWaiting':
            case 'completedWaiting':
                return 'timer-12';
        }
    };

    //gets the space option based on props
    const getSpace = space => spaceModes[space] || spaceModes.medium;

    //step objects
    export let steps = [];
    //key of current selected step
    export let current = null;
    //if we should show line connectors between items
    export let connected = false;
    //if we should show line connector at the beginning
    export let connectorStart = false;
    //if we should show line connector at the end
    export let connectorEnd = false;
    //wrap mode
    export let wrap = false;
    //space
    export let space = spaceModes.medium;
    //aria-labelledby attribute
    export let ariaLabelledBy;
    // disables the buttons
    export let disabled = false;
    // if container uses arrow-key navigation, step tabindex depends on whether focus is inside container or not
    export let containerHasFocus;
    // if container uses arrow-key navigation, this step should be the initial tabstop
    export let firstFocusableKey;

    const dispatch = createEventDispatcher();

    /**
     * Creates aria-label content taken directly from step object if specified or
     * composed from step label, state and index
     * @param {Object} step step object
     * @param {String} index index of the step
     * @returns {String} text for button aria-label attribute
     */
    function getAriaLabel(step, index) {
        if (step.ariaLabel) {
            return step.ariaLabel;
        } else {
            const label = getLabel(step, index);
            const ariaState = stateAriaLabels[step.state] || '';
            return `${label} ${ariaState}`;
        }
    }

    const handleClick = step => {
        if (step.disabled) {
            return;
        }
        dispatch('move', { key: step.key });
    };

    /**
     * Get tabindex for the step
     * @param {*} key
     * @param {Boolean} isStepDisabled
     * @returns {String?}
     */
    function getTabindex(key, isStepDisabled) {
        if (typeof containerHasFocus !== 'undefined') {
            //container uses arrow-key loop and controls focus
            if (!containerHasFocus && !isStepDisabled) {
                if (firstFocusableKey === key || typeof firstFocusableKey === 'undefined') {
                    return '0';
                }
            }
            return '-1';
        }
        return void 0;
    }
</script>

<style>
    /* Color scheme mixins */
    @define-mixin hover-focus-visible-default {
        &:hover:not([disabled]),
        &:focus-visible {
            background-color: var(--color-brand-light);
            border-color: var(--color-brand-hover);
            color: var(--color-brand-hover);
        }
    }
    @define-mixin hover-focus-visible-visited {
        &:hover:not([disabled]),
        &:focus-visible {
            background-color: var(--color-brand-light);
            border-color: var(--color-brand-hover);
            color: var(--color-brand-hover);
        }
    }
    @define-mixin hover-focus-visible-completed {
        &:hover:not([disabled]),
        &:focus-visible {
            background-color: var(--color-brand-hover);
            border-color: var(--color-brand-hover);
            color: var(--color-text-inverted);
        }
    }

    @define-mixin focus-visible-outline-spacing $borderWidth {
        &:focus-visible::after {
            top: calc(-0.625rem - $borderWidth);
            right: calc(-0.625rem - $borderWidth);
            bottom: calc(-0.625rem - $borderWidth);
            left: calc(-0.625rem - $borderWidth);
        }
    }

    ol {
        --size-default: 5rem;
        --hitboxSize-default: 6.25rem;
        --hitboxSize-large: 7.25rem;
        --margin-default: 0.625rem;
        --margin-large: 1.125rem;
        list-style-type: none;
        margin: 0;
        padding: 0;
        display: inline-flex;
        align-items: flex-start;
        justify-content: center;
        background-clip: content-box;
        position: relative;
        vertical-align: top;

        &.wrapped {
            flex-wrap: wrap;
            align-content: flex-start;
            justify-content: flex-start;

            & li {
                margin-bottom: 1rem; /* add space for current indicator */
            }
        }

        & li {
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            margin: var(--margin-default);
        }

        &.space-large li {
            margin: var(--margin-large);
        }

        &.space-large li button:before {
            --hitbox-size: var(--hitboxSize-large);
        }

        &.connected {
            &::after {
                content: '';
                position: absolute;
                display: block;
                height: 0.125rem;
                width: calc(100% - 2 * var(--margin-default));
                top: calc(50% - 0.0625rem);
                background-color: currentColor;
                z-index: -1;
            }

            &.connector-start::after {
                width: calc(100% - var(--margin-default));
                inset-inline-start: 0;
            }

            &.connector-end::after {
                width: calc(100% - var(--margin-default));
                inset-inline-end: 0;
            }

            &.connector-start.connector-end::after {
                left: auto;
                right: auto;
                width: 100%;
            }

            &.space-large {
                &::after {
                    width: calc(100% - 2 * var(--margin-large));
                }
                &.connector-start::after {
                    width: calc(100% - var(--margin-large));
                }
                &.connector-end::after {
                    width: calc(100% - var(--margin-large));
                }
                &.connector-start.connector-end::after {
                    width: 100%;
                }
            }
        }
    }

    .step {
        &:not([disabled]) {
            cursor: pointer;
        }

        display: flex;
        justify-content: center;
        align-items: center;

        position: relative;

        height: var(--size-default);
        width: var(--size-default);
        border-style: solid;
        border-radius: var(--radius-circular);
        border-width: var(--border-thin);
        border-color: var(--color-text-disabled);

        /*Fixes firefox button jumps*/
        padding: 0;

        @add-mixin bold;
        font-size: var(--fontsize-body-s);

        background-color: var(--color-bg-default);
        color: var(--color-text-disabled);

        @add-mixin hover-focus-visible-default;
        @add-mixin focus-visible-outline-spacing var(--border-thin);

        &:before {
            --hitbox-size: var(--hitboxSize-default);
            --offset: calc(var(--hitbox-size) - var(--size-default)) / 2;

            width: var(--hitbox-size);
            height: var(--hitbox-size);

            position: absolute;
            top: var(--offset);
            left: var(--offset);

            content: '';
        }

        &.visited,
        &.visitedWaiting {
            border-width: var(--border-medium);
            color: var(--color-text-default);
            border-color: var(--color-text-default);
            @add-mixin hover-focus-visible-visited;
            @add-mixin focus-visible-outline-spacing var(--border-medium);
        }

        &.completed,
        &.correct,
        &.partial,
        &.incorrect,
        &.completedWaiting {
            background-color: var(--color-bg-inverted);
            color: var(--color-text-inverted);
            border-color: var(--color-bg-inverted);
            @add-mixin hover-focus-visible-completed;
            @add-mixin focus-visible-outline-spacing var(--border-thin);
        }

        &.disabled {
            border: none;
        }

        &.current .indicator {
            position: absolute;
            display: block;
            bottom: -1.75rem;
            z-index: 1;
            padding: 0 0.125rem;
            color: var(--color-text-default);
            background-color: var(--color-bg-default);

            & > :global(svg) {
                display: block;
            }
        }

        &.current:focus-visible .indicator {
            color: var(--color-brand-hover);
        }

        &.current:hover:not([disabled]) .indicator {
            color: var(--color-brand-hover);
        }

        /* Focus styles */
        @add-mixin outline-focus;
        &:focus-visible::after {
            border-radius: var(--radius-circular);
        }

        & span.step-label {
            max-width: 3.75rem;
            overflow: hidden;
            white-space: nowrap;
        }

        & .badge {
            position: absolute;
            top: -1.25rem;
            right: -1.25rem;
            width: 3rem;
            height: 3rem;
            border-radius: 100%;
            color: var(--color-text-inverted);
            border: var(--border-medium-plus) solid var(--color-bg-default);
            display: flex;
            justify-content: center;
            align-items: center;
        }

        &.correct .badge {
            background-color: var(--color-success);
        }

        &.incorrect .badge {
            background-color: var(--color-alert);
        }

        &.partial .badge {
            background-color: var(--color-partial);
        }

        &.visitedWaiting .badge,
        &.completedWaiting .badge {
            background-color: var(--color-text-disabled);
        }
    }

    /* Inverted parent */
    :global(.inverted) .step {
        background-color: var(--color-bg-inverted);
        color: var(--color-text-disabled-inverted);
        border-color: var(--color-text-disabled-inverted);
        @add-mixin hover-focus-visible-default;

        &.visited,
        &.visitedWaiting {
            background-color: var(--color-bg-inverted);
            color: var(--color-text-inverted);
            border-color: var(--color-text-inverted);
            @add-mixin hover-focus-visible-visited;
        }

        &.completed,
        &.correct,
        &.partial,
        &.incorrect,
        &.completedWaiting {
            background-color: var(--color-bg-default);
            color: var(--color-text-default);
            border-color: var(--color-bg-default);
            @add-mixin hover-focus-visible-completed;
        }

        &.current .indicator {
            color: var(--color-text-inverted);
            background-color: var(--color-bg-inverted);
        }

        &.current:hover .indicator {
            color: var(--color-brand-hover);
        }

        &:focus-visible::after {
            border-color: var(--color-border-focus-inverted);
        }

        & .badge {
            border-color: var(--color-border-default);
        }
    }
</style>

<ol
    aria-labelledby={ariaLabelledBy}
    class:connected={connected && !wrap}
    class:wrapped={wrap}
    class:connector-start={connectorStart}
    class:connector-end={connectorEnd}
    class="space-{getSpace(space)}">
    {#each steps as step, index (step.key)}
        <li>
            <button
                class="step {getState(step)}"
                class:current={step.key === current}
                aria-disabled={disabled || step.disabled || getState(step) === 'disabled' || void 0}
                aria-current={step.key === current ? 'location' : null}
                aria-label={getAriaLabel(step, index)}
                on:click={() => handleClick(step)}
                disabled={disabled || step.disabled || getState(step) === 'disabled' || void 0}
                tabindex={getTabindex(step.key, disabled || step.disabled, firstFocusableKey, containerHasFocus)}>
                {#if step.key === current}
                    <span class="indicator" aria-hidden="true">
                        <Icon name="current-indicator-12" />
                    </span>
                {/if}
                {#if getBadgeIcon(step)}
                    <span class="badge">
                        <Icon name={getBadgeIcon(step)} ariaHidden={true} />
                    </span>
                {/if}
                {#if step.icon}
                    <Icon name={step.icon} ariaHidden={true} />
                {:else}<span class="step-label" aria-hidden="true">{getLabel(step, index)}</span>{/if}
            </button>
        </li>
    {/each}
</ol>
